/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {SpoHeaderDataModel} from '../../../spo/models/spo-header-data.model';
import {AbstractControl, UntypedFormBuilder, ValidatorFn, Validators} from '@angular/forms';
import {PartnerDetailModel} from '../../../api/ms-specialorders/contracts/partner-detail.model';
import {ShipToPartyBaseModel} from '../../../api/ms-specialorders/contracts/ship-to-party-base.model';
import {
    SupplyingPackingPlantBaseModel
} from '../../../api/ms-specialorders/contracts/supplying-packing-plant-base.model';
import {ReasonBaseModel} from '../../../api/ms-specialorders/contracts/reason-base.model';
import {TransportServiceBaseModel} from '../../../api/ms-specialorders/contracts/transport-service-base.model';
import {
    NgbCalendar,
    NgbDate,
    NgbDateParserFormatter,
    NgbDatepicker,
    NgbDatepickerConfig,
    NgbModal
} from '@ng-bootstrap/ng-bootstrap';
import {forkJoin, Observable, of, Subscription, timer} from 'rxjs';
import {PartnerService} from '../../../api/ms-specialorders/partner/partner.service';
import {ReasonService} from '../../../api/ms-specialorders/reason/reason.service';
import {DatepickerHelperService} from '../../utils/datepicker-helper/datepicker-helper.service';
import {TransportModeBaseModel} from '../../../api/ms-specialorders/contracts/transport-mode-base.model';
import {SpecialOrderStatus} from '../../../api/ms-specialorders/contracts/special-order-status.enum';
import {LoadingService} from '../../loading/loading-service/loading.service';
import {MaterialListService} from '../../material-list/service/material-list.service';
import {MaterialService} from '../../../api/ms-specialorders/material/material.service';
import {
    SpecialOrderLeadTimeValidationResultModel
} from '../../../api/ms-specialorders/contracts/special-order-lead-time-validation-result.model';
import * as moment from 'moment';
import {
    SpecialOrderLeadTimeValidationModel
} from '../../../api/ms-specialorders/contracts/special-order-lead-time-validation.model';
import {SeverityEnum} from '../../../api/generic/contracts/severity.enum';
import {MessageBaseModel} from '../../../api/generic/contracts/message-base.model';
import {
    SpecialOrderMaterialDetailModel
} from '../../../api/ms-specialorders/contracts/special-order-material-detail.model';
import {
    SpecialOrderPositionValidationModel
} from '../../../api/ms-specialorders/contracts/special-order-position-validation.model';
import {
    SpecialOrderPositionValidationResultModel
} from '../../../api/ms-specialorders/contracts/special-order-position-validation-result.model';
import {SpecialOrderService} from '../../../api/ms-specialorders/specialorder/specialorder.service';
import {MaterialDetailModel} from '../../../api/ms-specialorders/contracts/material-detail.model';
import {ConfirmModalComponent} from '../../confirm-modal/deprecated-modals/confirm-modal.component';
import {AlertNotificationSeverity} from '../../../alert-notification-severity.model';
import {ConfirmModal} from '../../confirm-modal/deprecated-modals/confirm-modal.model';
import {TooltipEnum} from '../../tooltip/tooltip.enum';
import {ReferenceDataApi} from '../../../api/ms-specialorders/reference-data.api';
import {WarningsEnum} from '../../warnings/warnings.enum';
import {TypeaheadHelperService} from '../../services/typeahead-helper.service';
import {InputDropdownComponent} from '../../input/input-dropdown/input-dropdown.component';
import {InputDateComponent} from '../../input/input-date/input-date.component';
import {InputTextComponent} from '../../input/input-text/input-text.component';
import {InputComponent} from '../../input/input/input.component';
import {delay, distinctUntilChanged, filter} from 'rxjs/operators';
import {InputMessagesService} from '../../input/service/input-messages.service';
import {MandatoryMessageTypeEnum} from '../../input/service/mandatory-message-type.enum';
import {objectsValidator} from '../../input/input-validators';
import {
    SupplyingPackingPlantReportBaseModel
} from '../../../api/ms-specialorders/contracts/supplying-packing-plant-report-base.model';
import {ToastWrapperService} from '../../utils/toast-wrapper-service/toast-wrapper-service';
import {PartnerBaseModel} from '../../../api/ms-specialorders/contracts/partner-base.model';
import {StatefulComponent} from '../../stateful/stateful.component';
import {SpoDetailFormState} from './spo-detail-form-state';
import {ReferenceDataModel} from '../../../api/reference-data.model';
import {SpinnerService} from '../../spinner/spinner.service';

@Component({
    selector: 'app-spo-detail-form',
    templateUrl: './spo-detail-form.component.html',
    styleUrls: ['./spo-detail-form.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SpoDetailFormComponent extends StatefulComponent<SpoDetailFormState> implements OnInit, OnDestroy {
    /** SPO details to be showed in the input fields */
    @Input() spoDetails: SpoHeaderDataModel;
    /** SPO Material information to set initial warnings **/
    @Input() spoMaterials: SpecialOrderMaterialDetailModel[];
    /** Variable to hide/show component title */
    @Input() isEdit: boolean;
    /** Is SPO status open */
    @Input() spoStatus: string;
    /** Event emitter to output new/updated form values */
    @Output() updatedSpo = new EventEmitter<SpoHeaderDataModel>();
    /** Event emitter for when form is loading **/
    @Output() formIsLoading = new EventEmitter<boolean>();
    /** Event emitter for when the form is touched */
    @Output() formIsTouched = new EventEmitter<boolean>();
    @ViewChild('gridItem', {static: true}) gridItem: ElementRef;
    @ViewChild('d', {static: true}) datepicker: NgbDatepicker;
    /** Inputs variables */
    @ViewChild('partnerInput', {static: true}) partnerInput: InputDropdownComponent;
    @ViewChild('reasonInput', {static: true}) reasonInput: InputDropdownComponent;
    @ViewChild('shipToPartyInput', {static: true}) shipToPartyInput: InputDropdownComponent;
    @ViewChild('supplyingPackingPlantInput', {static: true}) supplyingPackingPlantInput: InputDropdownComponent;
    @ViewChild('transportModeInput', {static: true}) transportModeInput: InputDropdownComponent;
    @ViewChild('transportServiceInput', {static: true}) transportServiceInput: InputDropdownComponent;
    @ViewChild('requestedDateInput', {static: true}) requestedDateInput: InputDateComponent;
    @ViewChild('justificationInput', {static: true}) justificationInput: InputTextComponent;
    public readonly partnerInfoLabel = ['This form has selection-dependent inputs. This means that once you select one or more ' +
    'options, you must provide additional input related to the selected option.',
        'Note that if you make any changes to an input, the dependent ones will be cleaned.'];
    /** Supplying packing plant material validation variables **/
    public materialNumbers: string[] = [];
    public listMaterials: SpecialOrderMaterialDetailModel[] = [];
    public differentMaterials: boolean[] = [];
    /** Datepicker variables */
    public displayMonths = 2;
    public minDate = this.calendar.getToday();
    public requestedDateInUtc: number;
    public leadTimeValidationModel: SpecialOrderLeadTimeValidationModel;
    /** Enum variables **/
    TooltipEnum = TooltipEnum;
    WarningsEnum = WarningsEnum;
    /** Inputs validators */
    validators: { [key: string]: ValidatorFn[] } = {
        partner: [objectsValidator],
        reason: [objectsValidator],
        requestedDate: [objectsValidator],
        shipToParty: [objectsValidator],
        supplyingPackingPlant: [objectsValidator],
        transportMode: [objectsValidator],
        transportService: [objectsValidator],
        justification: [Validators.maxLength(256)]
    };
    /** Inputs messages variables */
    public partnerInputMessages = new Map<WarningsEnum, string[]>();
    public shipToPartyInputMessages = new Map<WarningsEnum, string[]>();
    public supplyingPackingPlantInputMessages = new Map<WarningsEnum, string[]>();
    public transportModeInputMessages = new Map<WarningsEnum, string[]>();
    public transportServiceInputMessages = new Map<WarningsEnum, string[]>();
    public justificationInputMessages = new Map<WarningsEnum, string[]>();
    public requestedDateInputMessages = new Map<WarningsEnum, string[]>();
    public reasonInputMessages = new Map<WarningsEnum, string[]>();
    private isFormTouched = false;
    /** Dropdown variables */
    private partnerOptions: PartnerDetailModel[] = [];
    private shipToPartyOptions: ShipToPartyBaseModel[] = [];
    private supplyingPackingPlantOptions: SupplyingPackingPlantBaseModel[] = [];
    private transportModeOptions: TransportModeBaseModel[] = [];
    private transportServiceOptions: TransportServiceBaseModel[] = [];
    private reasonOptions: ReasonBaseModel[] = [];
    /** Header values variable */
    private headerValues: SpoHeaderDataModel = {
        justification: null,
        partner: null,
        reason: null,
        requestedDate: null,
        shipToParty: null,
        supplyingPackingPlant: null,
        transportMode: null,
        transportService: null
    } as any;

    /** Subscriptions variable */
    private subscriptions = new Subscription();

    constructor(
            private formBuilder: UntypedFormBuilder,
            private partnerService: PartnerService,
            private referenceDataApi: ReferenceDataApi,
            private reasonService: ReasonService,
            private calendar: NgbCalendar,
            private ngbDateStruct: NgbDateParserFormatter,
            datepickerConfig: NgbDatepickerConfig,
            public datepickerHelper: DatepickerHelperService,
            private loadingService: LoadingService,
            private spinnerService: SpinnerService,
            private materialListService: MaterialListService,
            private materialService: MaterialService,
            private modalService: NgbModal,
            public specialOrderService: SpecialOrderService,
            private toastService: ToastWrapperService,
            public typeahead: TypeaheadHelperService,
            private inputMessagesService: InputMessagesService) {
        super();
        /** Start spinner loading*/
        this.loadingService.start();
        this.storeState({
            requestedDateValidation: {
                previousTransportMode: null,
                previousTransportService: null,
                previousSupplyingPackingPlant: null
            }
        });

    }

    get hasTransportServices(): boolean {
        return this.transportServiceOptions != null && this.transportServiceOptions.length > 0;
    }

    /** Listener to window resize events: determines the number of months showed in the datepicker */
    @HostListener('window:resize')
    onResize(): void {
        this.displayMonths = this.setDatepickerMonths(this.gridItem.nativeElement.offsetWidth);
    }

    ngOnInit(): void {
        /** Get initial data when SPO is not in open status */
        if (this.spoNotSubmitted()) {
            this.getInitialData(this.componentInitialSetup);
        } else {
            timer(0)
                    .subscribe(() => this.componentInitialSetup());
        }

        this.subscriptions.add(this.spinnerService
                .onGlobalChange()
                .pipe(
                        filter((isLoading) => !isLoading),
                        delay(0)
                )
                .subscribe(() => {
                    this.onResize();
                }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    /**
     * Gets the initial data for the dropdowns and stops loading spinner
     */
    getInitialData(callback: () => void = (): void => {
    }): void {
        forkJoin([
            this.partnerService.getPartnersOptions(),
            this.reasonService.getActiveReasonOptions()
        ])
                .subscribe((([partners, reasons]:
                                     [PartnerDetailModel[],
                                         ReasonBaseModel[]]): void => {
                    this.partnerOptions = partners;
                    this.reasonOptions = reasons;
                }))
                .add(() => {
                    timer(0)
                            .subscribe(() => callback());
                    /** Stop spinner loading*/
                    this.loadingService.stop();
                });
    }

    /**
     * Sets the new partner value
     * Calls {@link updatedSpo.emit()} to emit new header values
     * Calls {@link getShipToPartyOptions} to get the corresponding ship to party options
     */
    setPartnerValue(partner: PartnerBaseModel): void {
        if (this.spoDetails == null) {
            this.resetRelatedVariables();
        }
        if (this.spoNotSubmitted()) {
            if (partner == null || !this.isValidCodeOption(partner) || this.isDifferentCodeOption(partner, this.headerValues.partner)) {
                // {emitEvent: false} so that inputValueChange emits only once when disable() is called
                this.shipToPartyInput.setValue(null, {emitEvent: false});
                this.disableInput(this.shipToPartyInput);
            }

            if (this.isValidCodeOption(partner) && this.partnerOptions) {
                this.getShipToPartyOptions(partner.code);
            }

            this.headerValues.partner = partner;
            this.setShipToPartyValue(this.headerValues.shipToParty);
        }
    }

    /**
     * Manages the partner input status changes, adding errors and warnings as needed
     */
    managePartnerInputStateChange(partner: AbstractControl): void {
        this.setFormAsDirty(partner);
        this.inputMessagesService.mandatoryWarnings(partner,
                this.partnerInputMessages,
                MandatoryMessageTypeEnum.SAVE_AND_SEND);
        this.inputMessagesService.invalidInputValueError(partner,
                this.partnerInputMessages,
                this.partnerInput.label);
        this.partnerInputMessages = new Map(this.partnerInputMessages);
    }

    /**
     * Sets the new ship to party value
     * Calls {@link getSupplyingPackingPlantOptions} to get the corresponding supplying packing plant options from the backend
     * Calls {@link updatedSpo.emit()} to emit new header values
     */
    setShipToPartyValue(shipToParty: ShipToPartyBaseModel): void {
        if (this.spoNotSubmitted()) {
            if (shipToParty == null || !this.isValidCodeOption(shipToParty)
                    || this.isDifferentCodeOption(shipToParty, this.headerValues.shipToParty)) {
                // {emitEvent: false} so that inputValueChange emits only once when disable() is called
                this.supplyingPackingPlantInput.setValue(null, {emitEvent: false});
                this.disableInput(this.supplyingPackingPlantInput);
            }

            if (this.isValidCodeOption(shipToParty)
                    && (this.isDifferentCodeOption(shipToParty, this.headerValues.shipToParty) || this.headerValues.shipToParty == null)) {
                this.getSupplyingPackingPlantOptions(shipToParty.code);
            }

            this.headerValues.shipToParty = shipToParty;
            this.setSupplyingPackingPlant(this.headerValues.supplyingPackingPlant);
        }
    }

    /**
     * Manages the ship to party input status changes, adding errors and warnings as needed
     */
    manageShipToPartyInputStateChange(shipToParty: AbstractControl): void {
        this.setFormAsDirty(shipToParty);
        this.inputMessagesService.mandatoryWarnings(shipToParty,
                this.shipToPartyInputMessages,
                MandatoryMessageTypeEnum.SAVE_AND_SEND);
        this.inputMessagesService.invalidInputValueError(shipToParty,
                this.shipToPartyInputMessages,
                this.shipToPartyInput.label);
        this.partnerInputMessages = new Map(this.shipToPartyInputMessages);
        this.shipToPartyInputMessages = new Map(this.shipToPartyInputMessages);
    }

    /**
     * Sets the new supplying packing plant value
     * Calls {@link getTransportModeOptions} to get the corresponding transport mode options from the backend
     * Calls {@link updatedSpo.emit()} to emit new header values
     */
    setSupplyingPackingPlant(plant: SupplyingPackingPlantBaseModel): void {
        if (this.spoNotSubmitted()) {
            if (plant == null || !this.isValidCodeOption(plant)
                    || this.isDifferentCodeOption(plant, this.headerValues.supplyingPackingPlant)) {
                // {emitEvent: false} so that inputValueChange emits only once when disable() is called
                this.transportModeInput.setValue(null, {emitEvent: false});
                this.disableInput(this.transportModeInput);
            }

            const shipToParty: ShipToPartyBaseModel = this.headerValues.shipToParty;

            if (this.isValidCodeOption(plant) && this.isValidCodeOption(shipToParty)) {
                this.getTransportModeOptions(shipToParty.code, plant.code);
            }

            this.headerValues.supplyingPackingPlant = plant;
            this.setTransportMode(this.headerValues.transportMode);
        }
    }

    /**
     * Manages the supplying packing plant input status changes, adding errors and warnings as needed
     */
    manageSupplyingPackingPlantInputStateChange(plant: AbstractControl): void {
        this.setFormAsDirty(plant);
        this.inputMessagesService.mandatoryWarnings(plant,
                this.supplyingPackingPlantInputMessages,
                MandatoryMessageTypeEnum.SAVE_AND_SEND);
        this.inputMessagesService.invalidInputValueError(plant, this.supplyingPackingPlantInputMessages,
                this.supplyingPackingPlantInput.label);
        this.supplyingPackingPlantInputMessages = new Map(this.supplyingPackingPlantInputMessages);
    }

    /**
     * Sets the new transport mode value
     * Calls {@link getTransportServicesOptions} to get the corresponding transport service options from the backend
     * Calls {@link updatedSpo.emit()} to emit new header values
     */
    setTransportMode(mode: TransportModeBaseModel): void {
        if (this.spoNotSubmitted()) {
            if (mode == null || !this.isValidCodeOption(mode) || this.isDifferentCodeOption(mode, this.headerValues.transportMode)) {
                // {emitEvent: false} so that inputValueChange emits only once when disable() is called
                this.transportServiceInput.setValue(null, {emitEvent: false});
                this.requestedDateInput.setValue(null, {emitEvent: true});
                this.disableInput(this.requestedDateInput);
                this.disableInput(this.transportServiceInput);
            }

            const shipToParty: ShipToPartyBaseModel = this.headerValues.shipToParty;
            const supplyingPackingPlant: SupplyingPackingPlantBaseModel = this.headerValues.supplyingPackingPlant;

            // Check if new mode is different from header values
            const isDifferentOption = this.isDifferentCodeOption(mode, this.headerValues.transportMode)
                    || this.headerValues.transportMode == null;
            // Update header values to new mode
            this.headerValues.transportMode = mode;

            if (this.isValidCodeOption(mode)
                    && this.isValidCodeOption(shipToParty)
                    && this.isValidCodeOption(supplyingPackingPlant)
                    && (isDifferentOption)) {
                this.transportServiceInput.setValue(null, {emitEvent: true});
                this.requestedDateInput.setValue(null, {emitEvent: true});
                this.disableInput(this.requestedDateInput);
                this.getTransportServicesOptions(shipToParty.code, supplyingPackingPlant.code, mode.code);
            }

            this.setTransportService(this.headerValues.transportService);
        }
    }

    /**
     * Manages the transport mode input status changes, adding errors and warnings as needed
     */
    manageTransportModeInputStateChange(mode: AbstractControl): void {
        this.setFormAsDirty(mode);
        this.showRequiredForSendWarning(mode, this.transportModeInput);
        this.inputMessagesService.mandatoryWarnings(mode,
                this.transportModeInputMessages,
                MandatoryMessageTypeEnum.SEND);
        this.inputMessagesService.invalidInputValueError(mode,
                this.transportModeInputMessages,
                this.transportModeInput.label);
        this.transportModeInputMessages = new Map(this.transportModeInputMessages);
    }

    /**
     * Sets the new transport service value and enables the justification and / or the requested date inputs
     * Calls {@link updatedSpo.emit()} to emit new header values
     */
    setTransportService(service: TransportServiceBaseModel): void {
        if (this.spoNotSubmitted()) {
            if (service == null || !this.isValidCodeOption(service)
                    || this.isDifferentCodeOption(service, this.headerValues.transportService)) {
                // {emitEvent: false} so that inputValueChange emits only once when disable() is called
                this.justificationInput.setValue(null, {emitEvent: false});
                this.disableInput(this.justificationInput);

                if (this.headerValues.transportMode != null && this.hasTransportServices) {
                    this.requestedDateInput.setValue(null, {emitEvent: false});
                    this.disableInput(this.requestedDateInput);
                }
                this.requestedDateInUtc = this.headerValues.requestedDate;
            }

            this.headerValues.transportService = service;

            if (this.isValidCodeOption(service)) {
                if (service.requiresJustification) {
                    this.justificationInput.enable();
                }
                this.requestedDateInput.enable();

                this.manageRequestedDateLeadTime();
            }
            timer(0)
                    .subscribe(() => this.updatedSpo.emit(this.headerValues));
        }
    }

    /**
     * Manages the transport service input status changes, adding errors and warnings as needed
     */
    manageTransportServiceInputStateChange(service: AbstractControl): void {
        this.setFormAsDirty(service);
        this.showRequiredForSendWarning(service, this.transportServiceInput);
        this.inputMessagesService.mandatoryWarnings(service,
                this.transportServiceInputMessages,
                MandatoryMessageTypeEnum.SEND);
        this.inputMessagesService.invalidInputValueError(service,
                this.transportServiceInputMessages,
                this.transportServiceInput.label);
        this.transportServiceInputMessages = new Map(this.transportServiceInputMessages);
    }

    /**
     * Sets the new justification value
     * Calls {@link updatedSpo.emit()} to emit new header values
     */
    setJustification(justification: string): void {
        if (this.spoNotSubmitted()) {
            this.headerValues.justification = justification;
            this.updatedSpo.emit(this.headerValues);
        }
    }

    /**
     * Manages the justification input status changes, adding errors and warnings as needed
     */
    manageJustificationInputStateChange(justification: AbstractControl): void {
        this.setFormAsDirty(justification);
        this.showRequiredForSendWarning(justification, this.justificationInput);
        this.inputMessagesService.mandatoryWarnings(justification,
                this.justificationInputMessages,
                MandatoryMessageTypeEnum.SEND);
        this.justificationInputMessages = new Map(this.justificationInputMessages);
    }

    /**
     * Sets the new requested date value
     * Calls {@link updatedSpo.emit()} to emit new header values
     */
    setRequestedDate(date: NgbDate): void {
        if (this.spoNotSubmitted()) {
            this.headerValues.requestedDate = this.datepickerHelper.convertNgbDateToUtcTimestamp(date);
            this.updatedSpo.emit(this.headerValues);
        }
    }

    /**
     * Manages the requested date input status changes, adding errors and warnings as needed
     */
    manageRequestedDateInputStateChange(date: AbstractControl): void {
        this.setFormAsDirty(date);
        this.showRequiredForSendWarning(date, this.requestedDateInput);
        this.inputMessagesService.mandatoryWarnings(date,
                this.requestedDateInputMessages,
                MandatoryMessageTypeEnum.SEND);
        this.inputMessagesService.invalidInputValueError(date,
                this.requestedDateInputMessages,
                this.requestedDateInput.label, 'dd-mm-yyyy');
        this.requestedDateInputMessages = new Map(this.requestedDateInputMessages);
    }

    /**
     * Sets the new reason value
     * Calls {@link updatedSpo.emit()} to emit new header values
     */
    setReason(reason: ReasonBaseModel): void {
        if (this.headerValues.reason !== reason) {
            this.headerValues.reason = reason;
        }
        this.updatedSpo.emit(this.headerValues);
    }

    /**
     * Manages the reason input status changes, adding errors and warnings as needed
     */
    manageReasonInputStateChange(reason: AbstractControl): void {
        this.setFormAsDirty(reason);
        this.showRequiredForSendWarning(reason, this.reasonInput);
        this.inputMessagesService.mandatoryWarnings(reason,
                this.reasonInputMessages,
                MandatoryMessageTypeEnum.SEND);
        this.inputMessagesService.invalidInputValueError(reason,
                this.reasonInputMessages,
                this.reasonInput.label);
        this.reasonInputMessages = new Map(this.reasonInputMessages);
    }

    /**
     * Checks if the form was already interacted with.
     * If the form was not previously touched emits {@link formIsTouched} and updates {@link isFormTouched} value
     * @param state The interacted abstract control
     */
    setFormAsDirty(state: AbstractControl): void {
        if (state.dirty && !this.isFormTouched) {
            this.formIsTouched.emit(state.dirty);
            this.isFormTouched = state.dirty;
        }
    }

    /**
     * Determines the info warning level only when the input is enabled
     * @param control The respective abstract control
     * @param input The input component to show the warning
     */
    showRequiredForSendWarning(control: AbstractControl, input: InputComponent): void {
        if (!control.disabled) {
            input.warningLevel = WarningsEnum.INFO;
        } else {
            input.warningLevel = null;
        }
    }

    /**
     * Subscribes to transportMode, transportService values
     * Calls {@link manageValidateLeadTimeValidationReport} to set new state
     */
    manageRequestedDateLeadTime(): void {
        const transportModeValue = this.headerValues.transportMode;
        const transportServiceValue = this.headerValues.transportService;
        const supplyingPackingPlantValue = this.headerValues.supplyingPackingPlant;

        let transportModeIsDifferent = this.state.requestedDateValidation.previousTransportMode == null;
        if (!transportModeIsDifferent) {
            transportModeIsDifferent = this.isValidCodeOption(transportModeValue)
                    && transportModeValue.code !== this.state.requestedDateValidation.previousTransportMode.code;
        }

        let transportServiceIsDifferent = this.state.requestedDateValidation.previousTransportService == null;
        if (!transportServiceIsDifferent) {
            transportServiceIsDifferent = this.isValidCodeOption(transportServiceValue)
                    && transportServiceValue.code !== this.state.requestedDateValidation.previousTransportService.code;
        }

        let supplyingPackingPlantIsDifferent = this.state.requestedDateValidation.previousSupplyingPackingPlant == null;
        if (!supplyingPackingPlantIsDifferent) {
            supplyingPackingPlantIsDifferent = this.isValidCodeOption(supplyingPackingPlantValue)
                    && supplyingPackingPlantValue.code !== this.state.requestedDateValidation.previousSupplyingPackingPlant.code;
        }

        if (transportModeValue != null
                && ((this.transportServiceOptions.length === 0 && (transportModeIsDifferent || supplyingPackingPlantIsDifferent))
                        || (transportServiceValue != null && (transportModeIsDifferent || transportServiceIsDifferent)))) {
            this.validateLeadTime(this.headerValues.shipToParty,
                    this.headerValues.supplyingPackingPlant,
                    transportModeValue,
                    transportServiceValue);
        }

        this.storeState({
            requestedDateValidation: {
                previousTransportMode: transportModeValue,
                previousTransportService: transportServiceValue,
                previousSupplyingPackingPlant: supplyingPackingPlantValue
            }
        });
    }

    validateLeadTime(shipTpParty: ShipToPartyBaseModel,
                     supplyingPackingPlant: SupplyingPackingPlantReportBaseModel,
                     transportMode: TransportModeBaseModel,
                     transportService: TransportServiceBaseModel): void {
        this.leadTimeValidationModel = {
            shipToPartyCode: shipTpParty.code,
            supplyingPackingPlantCode: supplyingPackingPlant.code,
            transportModeCode: transportMode.code,
            transportServiceCode: transportService ? transportService.code : null,
            requestedDate: this.requestedDateInUtc
        };
        this.manageValidateLeadTimeValidationReport(this.leadTimeValidationModel);
    }

    /**
     * Subscribes to {@link SpecialOrderService} to validate the requested date
     * On success:
     * * Calls {@link setDatepickerMinDateFromLeadTime} to set datepicker minimum date
     * * Call {@link manageClearRequestedDateInput} to reset requested date input if needed
     * On error: shows toast with link to retry request
     * **/
    manageValidateLeadTimeValidationReport = (validationModel: SpecialOrderLeadTimeValidationModel): void => {
        this.formIsLoading.emit(true);
        this.subscriptions.add(
                this.specialOrderService.validateSpecialOrderRequestDateAndLeadTime(validationModel)
                        .subscribe((report: SpecialOrderLeadTimeValidationResultModel) => {
                            this.setDatepickerMinDateFromLeadTime(report);
                            this.manageClearRequestedDateInput(report);
                            this.manageValidateLeadTimeMessages(report.message);
                        }, (error) => {
                            setTimeout(() => {
                                this.toastService.error(`Something went wrong! Click <strong>here</strong> to try again.`, 'Error')
                                        .onTap.subscribe(() => {
                                    this.manageValidateLeadTimeValidationReport(this.leadTimeValidationModel);
                                });
                            });
                        })
                        .add(() => {
                            this.formIsLoading.emit(false);
                        }));
    };

    /**
     * Manages the clear requested date input with lead time report
     * @param report The lead time report
     */
    manageClearRequestedDateInput = (report: SpecialOrderLeadTimeValidationResultModel): void => {
        if (report.valid === null || report.valid === false) {
            this.requestedDateInput.reset();
        }
        this.requestedDateInput.enable();
    };

    /**
     * Manages warning toast concerning the lead time validation
     * @param message the message to be shown
     */
    manageValidateLeadTimeMessages = (message: MessageBaseModel): void => {
        if (message != null && message.severity != null && message.description != null) {
            switch (message.severity) {
                case SeverityEnum.ERROR:
                    this.toastService.error(message.description, 'Error', {disableTimeOut: true});
                    break;
                case SeverityEnum.WARNING:
                    this.toastService.warning(message.description, 'Warning');
                    break;
                case SeverityEnum.INFO:
                    this.toastService.info(message.description, 'Info', {disableTimeOut: true});
                    break;
            }
        }
    };

    /**
     * Sets the datepicker minimum date according to the lead time
     *
     * @param leadTimeReport The lead time report {@link SpecialOrderLeadTimeValidationResultModel}
     * **/
    setDatepickerMinDateFromLeadTime = (leadTimeReport?: SpecialOrderLeadTimeValidationResultModel): void => {
        if (leadTimeReport != null && leadTimeReport.minRequestedDate != null) {
            this.minDate = this.datepickerHelper.convertUtcTimestampToNgbDate(leadTimeReport.minRequestedDate);
        } else {
            this.minDate = this.datepickerHelper.convertUtcTimestampToNgbDate(moment.now());
        }
    };

    /**
     * Manages the supplying packing plant warning
     * Sets label and visibility
     */
    manageSupplyingPackingPlantWarning(invalidMaterials: string[], differentMaterials: boolean[]): void {
        const discard = 'Some materials will be discarded.';
        const changed = 'Some materials have changed properties.';

        this.manageSupplyingPackingPlantMaterialInlineMessage(invalidMaterials.length > 0, WarningsEnum.WARNING, discard);
        this.manageSupplyingPackingPlantMaterialInlineMessage(differentMaterials.includes(false), WarningsEnum.WARNING, changed);

        this.supplyingPackingPlantInputMessages = new Map(this.supplyingPackingPlantInputMessages);
    }

    /**
     * Updates material list material details
     * @param materialValidationResult the result from the material validation
     * @param listMaterials materials currently on the list
     */
    updateMaterialListAfterMaterialValidation(materialValidationResult: SpecialOrderPositionValidationResultModel[],
                                              listMaterials: SpecialOrderMaterialDetailModel[]): void {
        materialValidationResult?.forEach((resultMaterial) => {
            if (resultMaterial.material.valid) {
                listMaterials.forEach((listMaterial) => {
                    if (listMaterial.position != null && resultMaterial.row === listMaterial.position) {
                        const foundMaterial = listMaterials.find((material) => material.position === listMaterial.position);
                        this.updateDifferentMaterials(foundMaterial.material, resultMaterial.material.value);
                        foundMaterial.material = resultMaterial.material.value;
                    }
                });
            }
        });
        this.materialListService.refreshMaterialData(listMaterials);
    }

    /**
     * Updates the {@link differentMaterials} array
     * Calls {@link materialsHaveTheSameValues} to check if materials are the same
     */
    updateDifferentMaterials(firstMaterial: MaterialDetailModel, secondMaterial: MaterialDetailModel): void {
        this.differentMaterials.push(this.materialsHaveTheSameValues(firstMaterial, secondMaterial));
    }

    /**
     *  Checks if materials from different supplying packing plants have the same attribute values
     *  @return boolean if the materials are the same
     */
    materialsHaveTheSameValues(firstMaterial: MaterialDetailModel,
                               secondMaterial: MaterialDetailModel): boolean {
        return firstMaterial.number === secondMaterial.number
                && JSON.stringify(firstMaterial.shelfLifePeriodType) === JSON.stringify(secondMaterial.shelfLifePeriodType)
                && firstMaterial.shelfLife === secondMaterial.shelfLife
                && firstMaterial.minQuantity === secondMaterial.minQuantity
                && firstMaterial.active === secondMaterial.active
                && firstMaterial.salesUnit === secondMaterial.salesUnit
                && firstMaterial.description === secondMaterial.description
                && firstMaterial.latestVersion === secondMaterial.latestVersion
                && firstMaterial.version === secondMaterial.version;
    }

    /**
     * Creates a Position Validation Model from a given material list
     * @param materials the material list
     * @return the {@link SpecialOrderPositionValidationModel}[] for a specific special order
     */
    getSpecialOrderPositionValidationModel(materials: SpecialOrderMaterialDetailModel[]): SpecialOrderPositionValidationModel[] {
        const materialValidationModel: SpecialOrderPositionValidationModel[] = [];
        if (materials && materials.length > 0) {
            materials.forEach((material) => {
                const currentMaterial: SpecialOrderPositionValidationModel = {
                    row: material.position,
                    number: material.material.number,
                    modelType: material.modelType,
                    lotNumber: material.lotNumber ? material.lotNumber.toString() : null,
                    orderQuantity: material.orderedQuantity ? material.orderedQuantity.toString() : null,
                    mcdrNumber: material.mcdrNumber ? material.mcdrNumber.toString() : null
                };
                if (materialValidationModel
                        .filter((model) => model.row === currentMaterial.row && model.number === currentMaterial.number).length === 0) {
                    materialValidationModel.push(currentMaterial);
                }
            });
        }
        return materialValidationModel;
    }

    /**
     * Partners input filter function
     */
    searchPartners = (term: string): Observable<any[]> => {
        if (term != null && term.length > 0) {
            return of(this.partnerOptions.filter((partner) => (`${partner.code} - ${partner.name}`)
                    .toLowerCase()
                    .indexOf(term.toLowerCase()) > -1));
        }
        return of(this.partnerOptions);
    };

    /**
     * Ship to party input filter function
     */
    searchShipToParty = (term: string): Observable<any[]> => {
        if (term != null && term.length > 0) {
            return of(this.shipToPartyOptions.filter((shipToParty) => (`${shipToParty.code} - ${shipToParty.name}`)
                    .toLowerCase()
                    .indexOf(term.toLowerCase()) > -1));
        }
        return of(this.shipToPartyOptions);
    };

    /**
     * Ship to party input filter function
     */
    searchSupplyingPackingPlant = (term: string): Observable<any[]> => {
        if (term != null && term.length > 0) {
            return of(this.supplyingPackingPlantOptions.filter((plant) => (`${plant.code} - ${plant.name}`)
                    .toLowerCase()
                    .indexOf(term.toLowerCase()) > -1));
        }
        return of(this.supplyingPackingPlantOptions);
    };

    /**
     * Transport mode input filter function
     */
    searchTransportMode = (term: string): Observable<any[]> => {
        if (term != null && term.length > 0) {
            return of(this.transportModeOptions.filter((mode) => (`${mode.code} - ${mode.name}`)
                    .toLowerCase()
                    .indexOf(term.toLowerCase()) > -1));
        }
        return of(this.transportModeOptions);
    };

    /**
     * Transport service input filter function
     */
    searchTransportService = (term: string): Observable<any[]> => {
        if (term != null && term.length > 0) {
            return of(this.transportServiceOptions.filter((service) => (`${service.code} - ${service.name}`)
                    .toLowerCase()
                    .indexOf(term.toLowerCase()) > -1));
        }
        return of(this.transportServiceOptions);
    };

    /**
     * Reason input filter function
     */
    searchReason = (term: string): Observable<any[]> => {
        if (term != null && term.length > 0) {
            return of(this.reasonOptions.filter((reason) => (`${reason.code} - ${reason.name}`)
                    .toLowerCase()
                    .indexOf(term.toLowerCase()) > -1));
        }
        return of(this.reasonOptions);
    };

    /**
     * Set the datepicker number of months
     * Window width bigger than 404px: show 2 months
     * Window width smaller than 404px: show only 1 month
     */
    setDatepickerMonths(inputWidth: number): number {
        return inputWidth > 404 ? 2 : 1;
    }

    /**
     * Gets the ship to party options
     * @param partner The selected partner code
     */
    private getShipToPartyOptions = (partner: string): void => {
        this.shipToPartyOptions = this.partnerOptions
                .find((p) => p.code === partner)
                .shipToParties.filter((stp) => stp.active);

        if (this.shipToPartyOptions.length > 1 && this.shipToPartyInput.isDisabled) {
            this.shipToPartyInput.enable();
        } else if (this.shipToPartyOptions.length === 1) {
            if (this.headerValues.shipToParty == null
                    || this.isDifferentCodeOption(this.headerValues.shipToParty, this.shipToPartyOptions[0])) {
                this.shipToPartyInput.setValue(this.shipToPartyOptions[0]);
            }
            this.disableInput(this.shipToPartyInput);
        }
    };

    /**
     * Gets the supplying packing plant options
     * @param shipToParty the selected ship to party
     */
    private getSupplyingPackingPlantOptions = (shipToParty: string): void => {
        this.formIsLoading.emit(true);
        this.subscriptions.add(
                this.referenceDataApi.getActiveSupplyingPackingPlants(shipToParty)
                        .pipe(distinctUntilChanged())
                        .subscribe((options: SupplyingPackingPlantBaseModel[]) => {
                            this.supplyingPackingPlantOptions = options;
                            if (options.length === 1) {
                                if (this.headerValues.supplyingPackingPlant == null
                                        || this.isDifferentCodeOption(this.headerValues.supplyingPackingPlant, options[0])) {
                                    this.supplyingPackingPlantInput.setValue(options[0]);
                                }
                                this.disableInput(this.supplyingPackingPlantInput);
                            } else if (options.length > 1 && this.supplyingPackingPlantInput.isDisabled) {
                                this.supplyingPackingPlantInput.enable();
                            }
                        })
                        .add(() => {
                            this.formIsLoading.emit(false);
                        }));
    };

    /**
     * Get transport mode options
     * @param shipToParty The selected ship to party code
     * @param plant The selected ssupplying packing plant code
     */
    private getTransportModeOptions = (shipToParty: string, plant: string): void => {
        this.formIsLoading.emit(true);
        this.subscriptions.add(
                this.referenceDataApi.getActiveTransportModes(shipToParty, plant)
                        .subscribe((options: TransportModeBaseModel[]) => {
                            this.transportModeOptions = options;
                            if (options.length === 1) {
                                if (this.headerValues.transportMode == null
                                        || this.isDifferentCodeOption(this.headerValues.transportMode, options[0])) {
                                    this.transportModeInput.setValue(options[0]);
                                }
                                this.disableInput(this.transportModeInput);
                            } else if (options.length > 1 && this.transportModeInput.isDisabled) {
                                this.transportModeInput.enable();
                            }
                        })
                        .add(() => {
                            this.formIsLoading.emit(false);
                        })
        );
    };

    /**
     * Retrieves the transport service options
     * @param shipToParty The selected ship to party code
     * @param supplyingPackingPlant The selected supplying packing plant code
     * @param transportMode The selected Transport mode code
     */
    private getTransportServicesOptions = (shipToParty: string, supplyingPackingPlant: string, transportMode: string): void => {
        this.formIsLoading.emit(true);
        this.subscriptions.add(
                this.referenceDataApi.getActiveTransportServices(
                        shipToParty,
                        supplyingPackingPlant,
                        transportMode)
                        .subscribe((options: TransportServiceBaseModel[]) => {
                            this.transportServiceOptions = options;
                            if (options.length !== 0) {
                                if (options.length === 1) {
                                    if (this.headerValues.transportService == null
                                            || this.isDifferentCodeOption(this.headerValues.transportService, options[0])) {
                                        this.transportServiceInput.setValue(options[0]);
                                    }
                                    this.disableInput(this.transportServiceInput);
                                } else if (options.length > 1 && this.transportServiceInput.isDisabled) {
                                    this.transportServiceInput.enable();
                                }
                                this.storeState({
                                    requestedDateValidation: {
                                        ...this.state.requestedDateValidation,
                                        previousTransportMode: transportMode
                                    }
                                });
                            } else {
                                this.headerValues.transportService = null;
                                this.manageRequestedDateLeadTime();

                                this.disableInput(this.transportServiceInput);

                                this.requestedDateInput.enable();

                                // {emitEvent: false} so that inputValueChange emits only once when disable() is called
                                this.justificationInput.setValue(null, {emitEvent: false});
                                this.disableInput(this.justificationInput);
                            }
                        })
                        .add(() => {
                            this.headerValues.requiresTransportService = this.hasTransportServices;
                            this.updatedSpo.emit(this.headerValues);
                            this.formIsLoading.emit(false);
                        })
        );
    };

    /**
     * If {@link spoDetails} is not null sets the initial component state.
     * Calls {@link setInitialFormValues} to set the initial form values
     * Calls {@link getInitialDropdownsValues} to get the initial dropdowns values
     * Calls {@link validateSpecialOrderMaterials} to validate the material list (if the spo has materials)
     */
    private componentInitialSetup = (): void => {
        /** Set initial values */
        if (this.spoDetails != null) {
            this.headerValues = {...this.spoDetails};
            this.setInitialFormValues(this.spoDetails);
        }

        if (this.spoNotSubmitted()) {
            // If {@link spoMaterials} exist set it to listMaterials
            if (this.spoMaterials != null) {
                this.listMaterials = this.spoMaterials;
                this.validateSpecialOrderMaterials(this.spoDetails.supplyingPackingPlant?.code);
            }
            // Populate reasons and partners dropdowns
            this.getInitialDropdownsValues();
        } else {
            this.loadingService.stop();
        }

        /** Init supplying packing plant material validation mode **/
        this.manageSupplyingPackingPlantChange();

        /** Disable inputs according to the SPO status */
        this.disableInputs(this.spoStatus);

        /** Set partner **/
        this.initPartner();
    };

    /**
     *  Sets the form values
     *  @param spoDetail: SPO header detail information
     */
    private setInitialFormValues = (spoDetail: SpoHeaderDataModel): void => {
        if (spoDetail.reason != null) {
            this.reasonInput.setValue(spoDetail.reason);
        }

        if (spoDetail.requestedDate != null) {
            this.requestedDateInput.setValue(this.datepickerHelper
                    .convertUtcTimestampToNgbDate(spoDetail.requestedDate));
        }

        if (spoDetail.transportMode != null) {
            this.transportModeInput.setValue(spoDetail.transportMode);

            if (spoDetail.transportService != null) {
                this.transportServiceInput.setValue(spoDetail.transportService);

                if (spoDetail.transportService.requiresJustification) {
                    if (spoDetail.justification != null) {
                        this.justificationInput.setValue(spoDetail.justification);
                    }
                } else {
                    this.disableInput(this.justificationInput);
                }
            } else {
                this.disableInput(this.justificationInput);
                if (this.hasTransportServices) {
                    this.disableInput(this.requestedDateInput);
                }
            }
        } else {
            this.disableInput(this.transportServiceInput);
            this.disableInput(this.requestedDateInput);
            this.disableInput(this.justificationInput);
        }
        if (spoDetail.supplyingPackingPlant != null) {
            this.supplyingPackingPlantInput.setValue(spoDetail.supplyingPackingPlant);
        } else {
            this.disableInput(this.transportModeInput);
        }

        if (spoDetail.shipToParty != null) {
            this.shipToPartyInput.setValue(spoDetail.shipToParty);
        } else {
            this.disableInput(this.supplyingPackingPlantInput);
        }

        if (spoDetail.partner != null) {
            this.partnerInput.setValue(spoDetail.partner);
            this.disableInput(this.partnerInput);
        } else {
            this.disableInput(this.shipToPartyInput);
        }
    };

    /**
     * Manages the Supplying Packing Plant change
     * Calls {@link validateSpecialOrderMaterials} to validate the materials
     * Updates {@link MaterialListService} with new Supplying Packing Plant number
     * **/
    private manageSupplyingPackingPlantChange(): void {
        /** Subscription to get updated material list for supplying packing plant change management **/
        this.subscriptions.add(
                this.materialListService.materialData.subscribe((materials) => {
                    this.listMaterials = materials;
                }));

        this.subscriptions.add(
                this.supplyingPackingPlantInput.inputValueChange.subscribe((plant: any) => {
                    if (this.isValidCodeOption(plant)) {
                        if (this.spoNotSubmitted()) {
                            this.validateSpecialOrderMaterials(plant.code);
                        }
                        this.materialListService.refreshSupplyingPackingPlantCode(plant.code);
                    }
                    // TODO: Add feedback to user (not valid option selected)
                }));
    }

    /**
     * Validates the Special Order Material list
     * Updates the {@link MaterialListService} invalid materials
     * Calls {@link getSpecialOrderPositionValidationModel} to set the validation model
     * Calls {@link manageSupplyingPackingPlantWarning} to set the supplying packing plant warning and label
     * @param supplyingPackingPlantCode the supplying packing plant code
     */
    private validateSpecialOrderMaterials(supplyingPackingPlantCode: string): void {
        const invalidMaterialNumbers: string[] = [];
        const materialValidationModel = this.getSpecialOrderPositionValidationModel(this.listMaterials);
        if (materialValidationModel.length > 0) {
            this.formIsLoading.emit(true);
            this.specialOrderService.validateSpecialOrderMaterials({
                supplyingPackingPlantCode: supplyingPackingPlantCode,
                specialOrderMaterials: materialValidationModel
            })
                    .subscribe((result) => {
                        this.updateMaterialListAfterMaterialValidation(result, this.listMaterials);
                        result.forEach((material) => {
                            if (!material.material.valid) {
                                invalidMaterialNumbers.push(material.material.original);
                            }
                            // Update material list service invalid materials
                            this.materialListService.refreshInvalidMaterials({materialNumbers: invalidMaterialNumbers});
                            this.materialListService.refreshMaterialsErrors(result);
                        });
                        this.manageSupplyingPackingPlantWarning(invalidMaterialNumbers, this.differentMaterials);
                    }, (error) => {
                        const errorModalInstance: ConfirmModalComponent = this.modalService
                                .open(ConfirmModalComponent, {centered: true}).componentInstance;
                        errorModalInstance.initModal({
                            titleText: 'Error',
                            subtitleText: 'Something went wrong! ' +
                                    'Unable to validate the material list for the new Supplying Packing Plant.',
                            alertNotificationSeverity: AlertNotificationSeverity.ERROR,
                            primaryButtonText: null,
                            secondaryButtonText: 'Close'
                        } as ConfirmModal);
                    })
                    .add(() => {
                        this.formIsLoading.emit(false);
                    });
        }
    }

    /**
     * Calls the methods to retrieve the dropdown options according to the {@link headerValues}
     */
    private getInitialDropdownsValues(): void {
        if (this.headerValues.partner != null) {
            this.getShipToPartyOptions(this.headerValues.partner.code);

            if (this.headerValues.shipToParty != null) {
                this.getSupplyingPackingPlantOptions(this.headerValues.shipToParty.code);

                if (this.headerValues.supplyingPackingPlant != null) {
                    this.getTransportModeOptions(this.headerValues.shipToParty.code, this.headerValues.supplyingPackingPlant.code);

                    if (this.headerValues.transportMode != null) {
                        this.getTransportServicesOptions(this.headerValues.shipToParty.code,
                                this.headerValues.supplyingPackingPlant.code,
                                this.headerValues.transportMode.code);
                    }
                }
            }
        }
    }

    /**
     * Calls disable() to a specific input, if that input if not disabled
     * @param input the input to disable
     */
    private disableInput(input: InputComponent): void {
        if (!input.isDisabled) {
            input.disable();
        }
    }

    /**
     * Determines if two objects have different code attributes
     * @param value1 the 1st object value
     * @param value2 the 2nd object value
     * @returns boolean if the objects have the same code attribute value
     */
    private isDifferentCodeOption(value1: any, value2: any): boolean {
        let isDifferent = true;

        if (value1 != null && value2 != null &&
                value1.constructor === value2.constructor &&
                value1.code != null && value2.code != null &&
                value1.code === value2.code) {
            isDifferent = false;
        }

        return isDifferent;
    }

    /**
     * Sets the partner value in case there's only 1 option
     */
    private initPartner(): void {
        if (this.partnerOptions.length === 1) {
            if (this.headerValues.partner == null || this.isDifferentCodeOption(this.headerValues.partner, this.partnerOptions[0])) {
                this.partnerInput.setValue(this.partnerOptions[0], {emitEvent: false});
            }
            this.disableInput(this.partnerInput);
        }
    }

    /**
     * Manages the supplying packing plant inline message regarding the material list
     * @param condition is the message should be add or removed
     * @param messageType the type of message (warning, error, etc)
     * @param message the message to show or remove
     */
    private manageSupplyingPackingPlantMaterialInlineMessage(condition: boolean, messageType: WarningsEnum, message: string): void {
        if (condition) {
            if (this.supplyingPackingPlantInputMessages.has(messageType)) {
                if (!this.supplyingPackingPlantInputMessages.get(messageType)
                        .includes(message)) {
                    this.supplyingPackingPlantInputMessages.get(messageType)
                            .push(message);
                }
            } else {
                this.supplyingPackingPlantInputMessages.set(messageType, [message]);
            }
        } else {
            if (this.supplyingPackingPlantInputMessages.has(messageType)) {
                const index = this.supplyingPackingPlantInputMessages.get(messageType)
                        .indexOf(message);
                if (index > -1) {
                    this.supplyingPackingPlantInputMessages.get(messageType)
                            .splice(this.supplyingPackingPlantInputMessages.get(messageType)
                                    .indexOf(message));
                }
            }
        }
    }

    /**
     * Determines if an spo has been submitted based on its status
     * @returns boolean is the spo has not submitted
     */
    private spoNotSubmitted = (): boolean => this.spoStatus !== SpecialOrderStatus.OPEN;

    /**
     * Disabled the form inputs based on the spo status
     * @param spoStatus the spo status
     */
    private disableInputs(spoStatus: string): void {
        if (spoStatus === SpecialOrderStatus.OPEN) {
            this.partnerInput.disable();
            this.supplyingPackingPlantInput.disable();
            this.shipToPartyInput.disable();
            this.transportModeInput.disable();
            this.justificationInput.disable();
            this.transportServiceInput.disable();
            this.requestedDateInput.disable();
            this.reasonInput.disable();
        } else if (spoStatus == null) {
            this.supplyingPackingPlantInput.disable();
            this.shipToPartyInput.disable();
            this.transportModeInput.disable();
            this.justificationInput.disable();
            this.transportServiceInput.disable();
            this.requestedDateInput.disable();
        }
    }

    /**
     * Determines is a option that contains a code attribute is valid
     * @param option the option to check
     * @returns boolean if the option is valid
     */
    private isValidCodeOption = (option: ReferenceDataModel): boolean => option != null && option.code != null;

    /**
     * This method puts the form clear.
     */
    private resetRelatedVariables(): void {
        this.headerValues.partner = null;
        this.headerValues.shipToParty = null;
        this.headerValues.supplyingPackingPlant = null;
        this.headerValues.transportMode = null;
        this.headerValues.requiresTransportService = null;
        this.headerValues.transportService = null;
        this.headerValues.justification = null;
        this.headerValues.requestedDate = null;
        this.requestedDateInput.disable();
    }
}
